// Client-side auth helpers — talk to the Nest auth controller at /api/auth/*.
//
// Microsoft Entra is the sole authentication path (ADR-0004 / issue #4) —
// there is no demo bypass. `signIn` triggers the full-page Entra flow.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch, apiUrl } from './api';

export interface SignInResult {
    ok: boolean;
    error?: string;
}

export interface SessionState {
    authenticated: boolean;
    user: {
        userId: string;
        email: string | null;
        name: string | null;
        authMethod: 'microsoft';
        /** The view keys the allowlist grants this user (resolved at callback). */
        personas?: string[];
        /**
         * The currently-active persona (ADR-0004). One persona governs every
         * read; switching it is a deliberate beat audited via OpsAuditLog.
         * The sidebar reads this to highlight the active row and persist the
         * persona across a hard reload.
         */
        activePersona?: string;
    } | null;
    microsoftConfigured: boolean;
    /** Dev-only skip-auth hatch is enabled on the server (NODE_ENV !== prod
     *  AND DEV_AUTH_SKIP=true). Surfaces the dev fixture sign-in shortcut on
     *  the login screen. Always false in production. */
    devAuthSkipEnabled?: boolean;
    /** The public showroom sign-in surface is enabled on the server
     *  (SHOWROOM_MODE=true). Surfaces the showroom account picker plus the
     *  shared-password field on the login screen. */
    showroomModeEnabled?: boolean;
}

export async function fetchSession(): Promise<SessionState> {
    try {
        const res = await apiFetch('/api/auth/session', { credentials: 'include' });
        if (!res.ok) {
            return {
                authenticated: false,
                user: null,
                microsoftConfigured: false,
                devAuthSkipEnabled: false,
            };
        }
        return (await res.json()) as SessionState;
    } catch {
        return {
            authenticated: false,
            user: null,
            microsoftConfigured: false,
            devAuthSkipEnabled: false,
        };
    }
}

/**
 * Shared React Query key for the signed-in session. Every consumer
 * (SessionProvider, the `/` auth gate, the login screen) reads through this
 * one key so React Query collapses them to a single in-flight request and a
 * single cache entry — instead of each firing its own `fetchSession`.
 */
export const SESSION_QUERY_KEY = ['auth', 'session'] as const;

/**
 * The session as a React Query. `fetchSession` never rejects (it maps any
 * failure to an unauthenticated state), so `data` is the source of truth and
 * `isPending` marks the initial hydration window. Consumers share the cache
 * via {@link SESSION_QUERY_KEY}; invalidate that key to force a refresh after
 * a persona switch.
 */
export function useSessionQuery(): UseQueryResult<SessionState> {
    return useQuery<SessionState>({
        queryKey: SESSION_QUERY_KEY,
        queryFn: fetchSession,
        // The session drives an access-control input (the persona switcher +
        // client route-grant), so it must self-correct rather than trust the
        // global 5-minute `staleTime` / `refetchOnWindowFocus: false` defaults.
        // A grant changed server-side (or a cookie swapped under a live SPA)
        // re-syncs on the next tab focus. The endpoint is tiny; refetching it is
        // cheap. `staleTime: 0` marks it always-stale so the focus refetch fires.
        staleTime: 0,
        refetchOnWindowFocus: true,
    });
}

/** A fixture user surfaced by the hardened dev fixture sign-in route. */
export interface DevFixtureUser {
    id: string;
    name: string;
    email: string;
    personas: string[];
}

/**
 * DEV-ONLY: list the fixture users the server will accept. Returns an empty
 * array if the dev fixture sign-in hatch is not enabled (the endpoint 404s),
 * so the UI can simply hide the dev section in that case.
 */
export async function listDevFixtureUsers(): Promise<DevFixtureUser[]> {
    try {
        const res = await apiFetch('/api/_dev/local-fixture-users', {
            credentials: 'include',
        });
        if (!res.ok) return [];
        const data = (await res.json()) as { users?: DevFixtureUser[] };
        return data.users ?? [];
    } catch {
        return [];
    }
}

/**
 * DEV-ONLY: mint a session by hitting the hardened dev fixture sign-in
 * route. The route accepts only a `userId` matching an Active `users`
 * directory row (the same allowlist that gates Entra logins); personas come
 * from that row, never the body. The route 404s in production, off-host, or
 * when DEV_AUTH_SKIP is unset (issue #89).
 */
export async function signInAsDevFixture(userId: string): Promise<SignInResult> {
    try {
        const res = await apiFetch('/api/_dev/local-fixture-signin', {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ userId: userId }),
        });
        if (!res.ok) return { ok: false, error: `dev_signin_failed_${res.status}` };
        return { ok: true };
    } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : 'dev_signin_failed' };
    }
}

export interface ShowroomAccount {
    id: string;
    name: string;
    email: string;
    /** Job title, shown on the picker so a visitor knows what they choose. */
    title: string;
    personas: string[];
}

/**
 * List the demonstration accounts the public showroom will accept. Returns an
 * empty array when the showroom surface is off (the endpoint 404s), so the UI
 * simply hides the section.
 */
export async function listShowroomAccounts(): Promise<ShowroomAccount[]> {
    try {
        const res = await apiFetch('/api/showroom/accounts', { credentials: 'include' });
        if (!res.ok) return [];
        const data = (await res.json()) as { accounts?: ShowroomAccount[] };
        return data.accounts ?? [];
    } catch {
        return [];
    }
}

/**
 * Sign in to the public showroom. Unlike the dev fixture hatch this needs a
 * credential: the shared showroom password, which the server compares against a
 * bcrypt hash. The account id must match the server's fixed table and the
 * personas come from that table, never from this call.
 */
export async function signInToShowroom(userId: string, password: string): Promise<SignInResult> {
    try {
        const res = await apiFetch('/api/showroom/login', {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ userId: userId, password: password }),
        });
        if (!res.ok) return { ok: false, error: `showroom_signin_failed_${res.status}` };
        return { ok: true };
    } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : 'showroom_signin_failed' };
    }
}

/**
 * Trigger the Microsoft Entra sign-in flow. Full-page navigates to the
 * server's /microsoft/start, which sets a flow cookie and 302s on to
 * Microsoft. Microsoft Entra is the only authentication path.
 */
export async function signIn(options?: { callbackUrl?: string }): Promise<SignInResult> {
    const callbackUrl = options?.callbackUrl ?? '/';

    if (typeof window === 'undefined') return { ok: false, error: 'no_window' };
    // Use apiUrl() so the redirect targets the server origin in a deployed
    // cross-origin build (web + server on different domains).
    const url = new URL(apiUrl('/api/auth/microsoft/start'), window.location.origin);
    url.searchParams.set('callbackUrl', callbackUrl);
    window.location.assign(url.toString());
    return { ok: true };
}

/**
 * Switch the active persona for the current session (ADR-0004 /
 * issue #100). The server validates the requested persona against the
 * granted list, updates the session cookie, and writes an OpsAuditLog
 * row recording the switch. On success the caller should refresh the
 * session context so the rest of the app sees the new persona.
 */
export async function switchPersona(
    persona: string,
): Promise<{ ok: boolean; status: number; error?: string }> {
    try {
        const res = await apiFetch('/api/auth/persona', {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ persona }),
        });
        if (!res.ok) {
            const body = (await res.json().catch(() => ({}))) as {
                message?: string;
            };
            return {
                ok: false,
                status: res.status,
                error: body.message ?? `persona_switch_failed_${res.status}`,
            };
        }
        return { ok: true, status: res.status };
    } catch (err) {
        return {
            ok: false,
            status: 0,
            error: err instanceof Error ? err.message : 'persona_switch_failed',
        };
    }
}

export async function signOut(options?: { callbackUrl?: string }): Promise<void> {
    try {
        await apiFetch('/api/auth/logout', {
            method: 'POST',
            credentials: 'include',
        });
    } catch {
        // Best-effort; cookie clear works even if the request fails because the
        // page will navigate away anyway.
    }
    const target = options?.callbackUrl ?? '/login';
    if (typeof window !== 'undefined') {
        window.location.assign(target);
    }
}
